import { OKTA_ENV } from 'features/auth/components/OktaRouter';
import env from 'utils/env';

const ENV_MAP = {
  P: 'prod',
  Q: 'int',
  D: 'dev'
};

export const NEWSPRESS_PUB_MAP = {
  WSJC: 'wsjcommerce',
  BAR: 'barrons',
  MKTW: 'mw',
  PENTAB: 'penta',
  MG: 'mansionglobal',
  WSJ: 'wsj',
  WSJO: 'wsjopinion',
  FN: 'fn',
  DJRJ: 'rn' // to match newspress origin
};

const PUB_MAP = {
  WSJC: 'wsjcommerce',
  BAR: 'barrons',
  MKTW: 'mw',
  PENTAB: 'penta',
  MG: 'mansionglobal',
  WSJ: 'wsj',
  WSJO: 'wsjopinion',
  FN: 'fn'
};

const MEDIA_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[4][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LC_ID_PATTERN = /lc-/;

const WORDPRESS_PROD_MAP = {
  FN: 'https://cms.fnlondon.com/wp-admin/post.php',
  PN: 'https://cms.penews.com/wp-admin/post.php'
};

const WORDPRESS_NONPROD_MAP = {
  FN: 'https://int-qa-fnlondon-wordpress-cms.vir-dev.onservo.com/wp-admin/post.php',
  PN: 'https://int-qa-penews-wordpress-cms.vir-dev.onservo.com/wp-admin/post.php'
};

// Checks content has an environment, if content is from production and app environment matches content environment
const envContentDevelopmentCheck = (contentEnv?: string | null) => {
  if (!contentEnv) return false;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  return env.OKTA_ENV !== OKTA_ENV.PRD && contentEnv === ENV_MAP.P;
};

export const getContentEnv = (sourceUrl: string) => {
  let contentUrl: URL;
  let contentEnv: string;
  try {
    contentUrl = new URL(sourceUrl);
  } catch (e) {
    console.warn(`Invalid URL ${sourceUrl}, cannot determine article environment.`);
    return null;
  }
  contentEnv = 'prod';
  if (contentUrl.origin.toLowerCase().includes('dev')) {
    contentEnv = 'dev';
  }
  if (contentUrl.origin.toLowerCase().includes('int')) {
    contentEnv = 'int';
  }
  return contentEnv;
};

export const getBrand = (product: string) => {
  let brand;
  if (product.toLowerCase().includes('wsj')) {
    brand = 'wsj';
  } else if (product.toLowerCase().includes('marketwatch')) {
    brand = 'marketwatch';
  } else if (product.toLowerCase().includes('barrons')) {
    brand = 'barrons';
  } else if (product.toLowerCase().includes('djrj')) {
    brand = 'djriskjournal';
  } else {
    brand = '';
  }
  return brand;
};

const getWordpressUrl = (id: string) => {
  const prefix = id.match(/^(PN|FN)/) as Array<string>;
  const postId = id.replace(/^(PN|FN)/, '');
  const baseUrl =
    (env.OKTA_ENV as string) === 'prd'
      ? WORDPRESS_PROD_MAP[prefix[0] as keyof typeof WORDPRESS_PROD_MAP]
      : WORDPRESS_NONPROD_MAP[prefix[0] as keyof typeof WORDPRESS_NONPROD_MAP];
  return `${baseUrl}?post=${postId}&action=edit`;
};

export const getArticleEnvFromId = (id: string) => {
  const envPrefix = id.split('-')[0];
  return ENV_MAP[envPrefix[1] as keyof typeof ENV_MAP];
};

export const getPublicationFromId = (id: string, map: Record<string, string>) => {
  const publicationAbbr = id.split('-')[1];
  return map[publicationAbbr];
};

const buildArticleNewspressUrl = (id?: string) => {
  if (id?.match(/W[PQD]-/)) {
    const longPostId = id.split('-')[2];
    const publication = getPublicationFromId(id, NEWSPRESS_PUB_MAP);
    const articleEnv = getArticleEnvFromId(id);
    const prodContentInDev = envContentDevelopmentCheck(articleEnv);
    const postId = Number(longPostId);
    if (!publication || prodContentInDev || !postId) {
      return null;
    }

    return `https://newspress${
      articleEnv === ENV_MAP.P ? '' : `.${articleEnv}`
    }.dowjones.io/${publication}/wp-admin/post.php?post=${postId}&action=edit&readonly=true`;
  }

  if (id?.match(/^(PN|FN)/)) return getWordpressUrl(id);
  return null;
};

const NP_PC_ID_PATTERN = {
  BUYSIDE: 'CP-WSJ-'
};

// TODO: make compatible with other publications
const buildProductCardNewspressUrl = (id?: string) => {
  if (id?.match(NP_PC_ID_PATTERN.BUYSIDE)) {
    const pcId = id.replace(NP_PC_ID_PATTERN.BUYSIDE, '');
    return `https://commerce-products.${(env.OKTA_ENV as string) === 'prd' ? '' : 'dev.'}dowjones.io/buyside/${pcId}`;
  }
  console.warn('Product card ID is invalid. Please adjust the function to support non-buyside publications.');

  return null;
};

// strip IM- from the narrator id (IM-3d850f07-635c-4b3c-b4b1-8bafd89d7052)
const buildNarratorUrl = (id?: string) => `https://narrator.wsj.dowjones.io/project/${id?.substring(3)}`;

export const buildMediaUrl = (sourceUrl: string, product: string, type: string, mediaId: string) => {
  if (mediaId.match(MEDIA_ID_PATTERN)) {
    const brand = getBrand(product);
    const mediaEnv = getContentEnv(sourceUrl);
    const prodContentInDev = envContentDevelopmentCheck(mediaEnv);

    if ((type !== 'audio' && type !== 'video') || prodContentInDev) {
      return null;
    }

    return `https://video-tools${
      mediaEnv === 'prod' ? '' : `.${mediaEnv}`
    }.shdsvc.dowjones.io/editor/bce/${type}/${brand}/edit/${mediaId}`;
  }

  return null;
};

export const buildCMSUrl = (id?: string, contentType?: string, sourceUrl?: string, product?: string) => {
  if (id?.startsWith('IM-')) return buildNarratorUrl(id);
  switch (contentType) {
    case 'commerceproduct':
      return buildProductCardNewspressUrl(id);
    case 'audio':
    case 'video':
      if (!id || !sourceUrl || !product) return null;
      return buildMediaUrl(sourceUrl, product, contentType, id);
    default:
      return buildArticleNewspressUrl(id);
  }
};

export const buildNewsgridUrl = (id: string) => {
  const articleEnv = getArticleEnvFromId(id);
  const publication = getPublicationFromId(id, PUB_MAP);
  if (!publication || !articleEnv || ((env.OKTA_ENV as string) !== 'prd' && articleEnv === ENV_MAP.P)) {
    return null;
  }

  return `https://newsgrid${articleEnv === 'prod' ? '' : `.${articleEnv}`}.dowjones.io/article/${id}`;
};

export const buildLiveCoverageUrl = (sourceUrl: string, rootId: string) => {
  if (rootId.match(LC_ID_PATTERN)) {
    const publication = rootId.split('-')[1];
    const articleEnv = getContentEnv(sourceUrl);
    const prodContentInDev = envContentDevelopmentCheck(articleEnv);
    if (prodContentInDev) {
      return null;
    }

    const eventId = rootId.split('-').slice(2).join('-');
    return `https://livecoverage${
      articleEnv === 'prod' ? '' : `.${articleEnv}`
    }.dowjones.io/${publication}/news/event/${eventId}`;
  }

  return null;
};

export const appEnv = env.OKTA_ENV;
